import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { useLogin } from "@/components/LoginModal";
import { TrackerA, TrackerDiv } from "@/components/Tracker";
import { BuyVipDialog } from "@/components/Vip";
import VipTag from "@/components/VipTag";
import { MATERIAL_VIP_TAG } from "@/constants/gaodingVip";
import { isBigCompanyByUser, VIP_RIGHT_IDENTIFICATION } from "@/constants/vip";
import useAppStore from "@/context/store";
import {
  getCurrentOrg,
  getCurrentVip,
  getGdUserDownloadCountInfo,
  getUserRightCount,
} from "@/services";
import { isGdMerged } from "@/utils/gaoding";
import {
  getGdProductDetail,
  isBigCompanyByOrg,
  NEW_VIP_RIGHT_CODE,
} from "@/utils/gaodingVip";
import getTimeDuration from "@/utils/getTimeDuration";
import {
  MATERIAL_DOWNLOAD_RECORD_PAGE,
  ORDER_INVOICE_PAGE,
  VIP_RIGHT_PAGE,
} from "@/utils/host";
import useRequest from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { Else, If, Then } from "react-if";
import styles from "./index.module.less";

export default function VipInfo() {
  const router = useRouter();
  const [hover, setHover] = useState(false);
  const { user, vips, config } = useAppStore(({ user, vips, config }) => ({
    user,
    vips,
    config,
  }));
  const { login } = useLogin();

  const { data: vipData } = useRequest(
    async () => {
      if (!user) {
        return;
      }

      if (isGdMerged(user)) {
        const orgInfo = await getCurrentOrg();
        const detail = getGdProductDetail(orgInfo);
        if (detail.materialProductMod) {
          const download = await getGdUserDownloadCountInfo();

          const expireTime = detail.materialProductMod.product_info.expire_date;

          return {
            isVip: true,
            almostExipired: dayjs(expireTime).diff(dayjs(), "day", true) < 3,
            expireTime: getTimeDuration({
              time: expireTime,
              afterText: "",
              reverse: true,
            }),
            download: {
              quantity: download.total,
              usage: download.used,
              left: download.total - download.used,
            },
            isBigCompany: isBigCompanyByOrg(orgInfo),
            vipTag: (
              <Image
                className={styles.vipTag}
                src={MATERIAL_VIP_TAG[detail.code]}
                alt="tag"
                height={20}
              />
            ),
          };
        }

        return {
          isVip: false,
        };
      }
      const { vips: effect_vips, material_vip_expire_at } =
        await getCurrentVip();

      if (effect_vips.length) {
        const download = await getUserRightCount([
          { right_code: VIP_RIGHT_IDENTIFICATION },
          { right_code: NEW_VIP_RIGHT_CODE.GD_MATERIAL_DOWNLOAD },
        ]);

        return {
          isVip: true,
          almostExipired:
            dayjs(material_vip_expire_at).diff(dayjs(), "day", true) < 3,
          expireTime: getTimeDuration({
            time: material_vip_expire_at,
            afterText: "",
            reverse: true,
          }),
          download: download[0],
          isBigCompany: isBigCompanyByUser(user),
          vipTag: <VipTag className={styles.vipTag} />,
        };
      }

      return {
        isVip: false,
      };
    },
    {
      refreshDeps: [user],
    }
  );

  const { isVip, expireTime, almostExipired, download, isBigCompany, vipTag } =
    vipData || {
      isVip: vips?.length > 0,
    };

  const isGdMergedUser = isGdMerged(user);

  const onBuy = useMemoizedFn((type, source_location) => {
    if (!user) {
      login({
        source_location: "头部商业化模块",
      });
      return;
    }
    if (isVip) {
      goRight();
    } else {
      BuyVipDialog.open({
        type,
        isGdDialog: true,
        source_location: source_location + "_查看方案",
        onOk() {
          router.reload();
        },
      });
    }
  });

  const onRenewClick = useCallback(
    (evt) => {
      evt.stopPropagation();
      BuyVipDialog.open({
        source_location: "顶部_立即续费",
        onOk() {
          router.reload();
        },
      });
    },
    [router]
  );

  const goRight = useCallback(() => {
    // router.push("/me/right");
    window.open(VIP_RIGHT_PAGE);
  }, []);

  return (
    <If condition={isVip}>
      <Then>
        <div
          className={classNames(styles.card, {
            [styles.isGdmerged]: isGdMergedUser,
          })}
          onClick={goRight}
        >
          <div className={styles.info}>
            <div className={styles.top}>
              <div className={styles.title}>花瓣素材</div>
              {vipTag}
            </div>
            {almostExipired ? (
              <div className={styles.subtitle}>
                VIP将在<span>{expireTime}</span>后到期
              </div>
            ) : (
              <div className={styles.subtitle}>
                可下载 <span>{download?.left || 0}</span>/
                {download?.quantity || 0}
              </div>
            )}
          </div>
          <div className={styles.action}>
            {almostExipired ? (
              <a className={styles.renew} onClick={onRenewClick}>
                <div className={styles.text}>立即续费</div>
                <HBIcon className={styles.icon} type="ic_chevron_right" />
              </a>
            ) : (
              <>
                <TrackerA
                  className={styles.link}
                  href={MATERIAL_DOWNLOAD_RECORD_PAGE}
                  target="_blank"
                  data-gd-click="resource_loc_click"
                  data-gd-expose="resource_loc_expose"
                  data-resource-type="文字链接"
                  data-resource-id="0"
                  data-resource-title="下载记录"
                  data-content-source="顶部"
                  onClick={(evt) => evt.stopPropagation()}
                >
                  下载记录
                </TrackerA>
                <div className={styles.split}></div>
                <TrackerA
                  className={styles.link}
                  href={ORDER_INVOICE_PAGE}
                  target="_blank"
                  data-gd-click="resource_loc_click"
                  data-gd-expose="resource_loc_expose"
                  data-resource-type="文字链接"
                  data-resource-id="0"
                  data-resource-title="订单列表"
                  data-content-source="顶部"
                  onClick={(evt) => evt.stopPropagation()}
                >
                  订单列表
                </TrackerA>
              </>
            )}
          </div>
        </div>
      </Then>
      <Else>
        <div
          className={classNames([styles.group, hover ? styles.hover : null])}
        >
          <TrackerDiv
            className={styles.personal}
            data-gd-click="resource_loc_click"
            data-gd-expose="resource_loc_expose"
            data-resource-type="文字链接"
            data-resource-id="0"
            data-resource-title="个人VIP"
            data-content-source="顶部"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={() => onBuy("personal", "顶部基础版VIP")}
          >
            <div className={styles.info}>
              <div className={styles.title}>{"基础版-素材VIP"}</div>
              <div className={styles.subtitle}>
                ¥{config?.gdPersonalVipPrice}
                /年起
              </div>
            </div>
            <div className={styles.button}>开通</div>
          </TrackerDiv>
          <TrackerDiv
            className={styles.enterprise}
            data-gd-click="resource_loc_click"
            data-gd-expose="resource_loc_expose"
            data-resource-type="文字链接"
            data-resource-id="0"
            data-resource-title="企业VIP"
            data-content-source="顶部"
            onClick={() => onBuy("company", "顶部团队版VIP")}
          >
            <div className={styles.info}>
              <div className={styles.title}>{"团队版-素材 VIP"}</div>
              <div className={styles.subtitle}>
                ¥ {config?.gdCompanyVipPrice}
                /年起
              </div>
            </div>
            <div className={styles.button}>开通</div>
          </TrackerDiv>
        </div>
      </Else>
    </If>
  );
}
